import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  TalentSheetView,
  TalentSheetLoading,
  TalentSheetNotFound,
} from '@adthlete/shared-frontend'
import { useAuth } from '@/hooks/useAuth'
import {
  useGetPublicTalentSheetQuery,
  useRecordTalentSheetViewMutation,
} from '@/redux/talentSheetsApi'

// A talent sheet a manager shared with brands. Public: brands see it before they have an
// account, and sign up when they message an athlete.

const VISITOR_KEY = 'athiosVisitorId'

/** A random ID kept in this browser, so the manager sees unique viewers */
const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(VISITOR_KEY, id)
    }
    return id
  } catch {
    return crypto.randomUUID()
  }
}

const TalentSheet = () => {
  const { hashedId } = useParams<{ hashedId: string }>()
  const navigate = useNavigate()
  const { marketer } = useAuth()
  const {
    data: sheet,
    isLoading,
    isError,
  } = useGetPublicTalentSheetQuery(hashedId!, {
    skip: !hashedId,
  })
  const [recordView] = useRecordTalentSheetViewMutation()

  useEffect(() => {
    if (!hashedId) return
    recordView({ sheetId: hashedId, visitorId: getVisitorId() }).catch(() => {})
  }, [hashedId, recordView])

  if (isLoading) return <TalentSheetLoading variant="brand" />
  if (isError || !sheet) return <TalentSheetNotFound variant="brand" />

  return (
    <TalentSheetView
      variant="brand"
      sheet={sheet}
      onAthleteClick={(athleteId) => navigate(`/sheets/${hashedId}/athletes/${athleteId}`)}
      topBar={
        <div className="flex items-center justify-between gap-3 text-sm">
          <img
            src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png"
            alt="Athios"
            className="h-7 w-auto"
          />
          {marketer?.id ? (
            <Link
              to="/"
              className="rounded-xl border border-border bg-card px-3 py-1.5 font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Go to your dashboard
            </Link>
          ) : (
            <Link
              to={`/signin?returnTo=${encodeURIComponent(`/sheets/${hashedId}`)}`}
              className="rounded-xl border border-border bg-card px-3 py-1.5 font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Sign in
            </Link>
          )}
        </div>
      }
      footer={
        sheet.athletes.length > 0 && (
          <p className="mt-10 text-center text-sm text-gray-600">
            See someone you'd like to work with? Open their profile to send them a message.
          </p>
        )
      }
    />
  )
}

export default TalentSheet
